import { Link } from 'react-router-dom';
import { isProduction } from '@/lib/api';

export default function AcceptableUse() {
  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="px-6 lg:px-16 py-6 flex justify-between items-center bg-charcoal">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
            Log In
          </Link>
          <Link to={isProduction() ? '/coming-soon' : '/register'} className="btn-primary px-6 py-2 text-sm">
            Get Started
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Acceptable Use Policy</h1>
        <p className="text-charcoal/60 mb-12">Last updated: January 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">1. Purpose</h2>
            <p className="text-charcoal/80 leading-relaxed">
              This Acceptable Use Policy ("AUP") outlines the rules and guidelines for using Venndela's
              affiliate marketing platform. This policy is designed to protect our users, maintain service
              quality, and ensure compliance with applicable laws and payment processor requirements.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">2. Prohibited Business Categories</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              The following business categories are <strong>strictly prohibited</strong> from using Venndela.
              Organizations in these categories will be immediately removed from the platform:
            </p>

            <div className="space-y-6">
              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Adult Content & Services</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Pornography or sexually explicit material</li>
                  <li>Adult entertainment services</li>
                  <li>Escort or companion services</li>
                  <li>Strip clubs or adult venues</li>
                  <li>Adult dating with explicit content</li>
                  <li>AI-generated adult content</li>
                </ul>
              </div>

              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Gambling & Gaming</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Online casinos and gambling platforms</li>
                  <li>Sports betting services</li>
                  <li>Lottery tickets or services</li>
                  <li>Fantasy sports with cash prizes</li>
                  <li>Games of skill with monetary rewards</li>
                  <li>Poker or similar games for money</li>
                </ul>
              </div>

              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Controlled Substances</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Illegal drugs or controlled substances</li>
                  <li>Drug paraphernalia or manufacturing equipment</li>
                  <li>Cannabis, marijuana, or THC products</li>
                  <li>CBD products exceeding legal THC limits</li>
                  <li>Substances designed to mimic illegal drugs (including kava)</li>
                  <li>Online pharmacies (without proper licensing)</li>
                </ul>
              </div>

              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Weapons & Dangerous Materials</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Firearms and ammunition (without proper licensing)</li>
                  <li>Illegal weapons or explosives</li>
                  <li>Toxic, radioactive, or flammable materials</li>
                  <li>Weapons modifications or accessories (illegal)</li>
                </ul>
              </div>

              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Financial Services (Unlicensed)</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Unlicensed money transmission</li>
                  <li>Cryptocurrency mining or staking operations</li>
                  <li>Initial coin offerings (ICOs)</li>
                  <li>Payday lending without proper licensing</li>
                  <li>Debt collection agencies</li>
                  <li>Shell banks or unlicensed financial institutions</li>
                  <li>Check cashing or money order services</li>
                </ul>
              </div>

              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Deceptive & Fraudulent Practices</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Pyramid schemes or Ponzi schemes</li>
                  <li>Multi-level marketing with recruitment-based commissions</li>
                  <li>"Get rich quick" schemes</li>
                  <li>Fake reviews or testimonial services</li>
                  <li>Reputation manipulation services</li>
                  <li>Counterfeit goods or knockoffs</li>
                  <li>Unauthorized use of intellectual property</li>
                </ul>
              </div>

              <div className="bg-rust/10 border border-rust/30 rounded-lg p-6">
                <h3 className="font-display font-bold text-rust mb-3">Illegal Activities</h3>
                <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                  <li>Any illegal products or services</li>
                  <li>Human trafficking or exploitation</li>
                  <li>Products from sanctioned countries or persons</li>
                  <li>Illegal imports or exports</li>
                  <li>Services facilitating illegal activities</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">3. Restricted Categories (Require Review)</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              The following categories may be permitted with additional review and documentation:
            </p>
            <div className="bg-tan/30 border border-tan rounded-lg p-6">
              <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
                <li>Cryptocurrency exchanges or wallets (licensed)</li>
                <li>Tobacco or e-cigarettes</li>
                <li>Alcohol (varies by jurisdiction)</li>
                <li>Telemedicine services</li>
                <li>Crowdfunding platforms</li>
                <li>Investment or brokerage services (licensed)</li>
                <li>Firearms dealers (properly licensed)</li>
                <li>Nutraceuticals and supplements</li>
              </ul>
            </div>
            <p className="text-charcoal/80 leading-relaxed mt-4">
              Contact <a href="mailto:compliance@venndela.com" className="text-rust hover:text-rust-light">compliance@venndela.com</a> for
              pre-approval if your business falls into a restricted category.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">4. Affiliate Marketing Restrictions</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Venndela supports <strong>single-tier affiliate marketing only</strong>. The following structures are prohibited:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-4">
              <li>Multi-level or network marketing structures</li>
              <li>Commissions for recruiting other affiliates</li>
              <li>"Downline" or "upline" commission arrangements</li>
              <li>Tiered recruitment bonuses</li>
              <li>Revenue sharing based on recruited affiliate performance</li>
            </ul>
            <div className="bg-sage/10 border border-sage/30 rounded-lg p-6">
              <h3 className="font-display font-bold text-sage mb-2">Allowed Structure:</h3>
              <p className="text-charcoal/80 text-sm">
                Affiliates earn commissions only on sales or conversions they directly generate through their
                unique referral links. Commission rates may vary based on sales volume, but recruitment of
                other affiliates cannot generate commissions.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">5. Prohibited Marketing Practices</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Organizations and affiliates are prohibited from:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li><strong>False Income Claims:</strong> Making unrealistic or guaranteed income promises</li>
              <li><strong>Misleading Advertising:</strong> Deceptive claims about products or earnings potential</li>
              <li><strong>Spam:</strong> Unsolicited bulk emails, messages, or communications</li>
              <li><strong>Cookie Stuffing:</strong> Forcing affiliate cookies without user interaction</li>
              <li><strong>Click Fraud:</strong> Generating fake clicks or conversions</li>
              <li><strong>Trademark Abuse:</strong> Bidding on competitor trademarks without permission</li>
              <li><strong>Fake Reviews:</strong> Creating or purchasing fake testimonials</li>
              <li><strong>Hidden Affiliate Disclosure:</strong> Failing to disclose affiliate relationships as required by FTC</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">6. Income Disclosure Requirements</h2>
            <div className="bg-tan/30 border border-tan rounded-lg p-6 mb-4">
              <p className="text-charcoal/80 leading-relaxed mb-4">
                All income claims must include appropriate disclaimers:
              </p>
              <ul className="list-disc pl-6 text-charcoal/80 space-y-2 text-sm">
                <li>Results are not typical and vary by individual effort</li>
                <li>Past performance does not guarantee future results</li>
                <li>Testimonials represent individual experiences only</li>
                <li>No guaranteed minimum earnings</li>
              </ul>
            </div>
            <p className="text-charcoal/80 leading-relaxed">
              Organizations must not encourage affiliates to make income claims that cannot be substantiated.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">7. Technical Restrictions</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">Users shall not:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Attempt to bypass security measures or access controls</li>
              <li>Use automated tools to scrape or harvest data</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Reverse engineer, decompile, or disassemble the platform</li>
              <li>Share API keys or access credentials</li>
              <li>Exceed rate limits or abuse API access</li>
              <li>Upload malicious code, viruses, or harmful files</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">8. Content Guidelines</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">Content uploaded to Venndela must not:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Contain hate speech, harassment, or discrimination</li>
              <li>Include personal information of others without consent</li>
              <li>Promote violence or illegal activities</li>
              <li>Contain malware, viruses, or malicious code</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">9. Reporting Violations</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              If you observe violations of this policy, please report them to:
            </p>
            <ul className="text-charcoal/80 space-y-1 mb-4">
              <li>Email: <a href="mailto:abuse@venndela.com" className="text-rust hover:text-rust-light">abuse@venndela.com</a></li>
              <li>Subject line: "AUP Violation Report"</li>
            </ul>
            <p className="text-charcoal/80 leading-relaxed">
              Include as much detail as possible, including organization name, description of the violation,
              and any supporting evidence.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">10. Enforcement</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Violations of this policy may result in:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li><strong>Warning:</strong> First offense for minor violations</li>
              <li><strong>Temporary Suspension:</strong> Account access restricted pending review</li>
              <li><strong>Permanent Termination:</strong> Immediate removal for serious violations</li>
              <li><strong>Forfeiture:</strong> Pending commissions may be forfeited</li>
              <li><strong>Legal Action:</strong> Referral to law enforcement if applicable</li>
            </ul>
            <p className="text-charcoal/80 leading-relaxed mt-4">
              Severe violations, including prohibited business categories, fraud, or illegal activity,
              will result in immediate termination without warning.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">11. Appeals</h2>
            <p className="text-charcoal/80 leading-relaxed">
              If you believe your account was suspended in error, you may appeal by contacting{' '}
              <a href="mailto:appeals@venndela.com" className="text-rust hover:text-rust-light">appeals@venndela.com</a> within
              30 days of the suspension notice. Include your account details and a detailed explanation of
              why you believe the decision was incorrect.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">12. Updates to This Policy</h2>
            <p className="text-charcoal/80 leading-relaxed">
              We may update this Acceptable Use Policy from time to time to reflect changes in our practices,
              payment processor requirements, or legal obligations. Material changes will be communicated via
              email or through the platform. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">13. Contact</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              For questions about this policy:
            </p>
            <ul className="text-charcoal/80 space-y-1">
              <li>Compliance: <a href="mailto:compliance@venndela.com" className="text-rust hover:text-rust-light">compliance@venndela.com</a></li>
              <li>Abuse reports: <a href="mailto:abuse@venndela.com" className="text-rust hover:text-rust-light">abuse@venndela.com</a></li>
              <li>General support: <a href="mailto:support@venndela.com" className="text-rust hover:text-rust-light">support@venndela.com</a></li>
            </ul>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 pt-8 border-t border-tan/30">
          <h3 className="font-display text-lg font-bold text-charcoal mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/legal/terms"
              className="text-rust hover:text-rust-light underline"
            >
              Terms of Service
            </Link>
            <Link
              to="/legal/privacy"
              className="text-rust hover:text-rust-light underline"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-display text-xl font-bold text-warm-white">
            venndela
          </Link>
          <p className="text-charcoal/50 text-sm">
            © {new Date().getFullYear()} Venndela. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
